import { CCReport, CCReportComp, CCReportCompProject, costPerDivision } from './construction-cost-report.type';
import {
  max,
  min,
  average,
  minDate,
  maxDate,
  getYearsAndMonthsInBetween,
  capitalize,
  calculateMapCenterAndZoom,
} from '../../common/utils/common.util';
import React from 'react';
import { Box, Grid, Stack } from '@mui/material';
import { Typography } from 'components/index';
import { parseISO } from 'date-fns';
import html2canvas from 'html2canvas';

export const extractMetrics = (report: CCReport | costPerDivision, includeSoftCost: boolean) => {
  const includedCompProjects = (report.comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);

  const budgetField = includeSoftCost ? 'totalBudgetWithSoftCost' : 'totalBudgetWithoutSoftCost';

  const comparablesWithoutEmptyGSF =
    includedCompProjects?.filter(
      project => project.grossBuildingArea !== null && project.grossBuildingArea !== undefined && project.grossBuildingArea != 0,
    ) ?? [];
  const comparablesWithoutEmptyNSF =
    includedCompProjects?.filter(project => project.netArea !== null && project.netArea !== undefined && project.netArea != 0) ?? [];
  const comparablesWithoutEmptyUnitCount =
    includedCompProjects?.filter(project => project.unitCount !== null && project.unitCount !== undefined && project.unitCount != 0) ?? [];
  const comparablesWithoutEmptyBudget =
    includedCompProjects?.filter(
      project => project[budgetField] !== null && project[budgetField] !== undefined && project[budgetField] != 0,
    ) ?? [];
  const comparablesWithoutEmptySiteArea =
    includedCompProjects?.filter(project => project.siteArea !== null && project.siteArea !== undefined && project.siteArea != 0) ?? [];
  const comparablesWithoutEmptyStoryCount =
    includedCompProjects?.filter(project => project.storyCount !== null && project.storyCount !== undefined && project.storyCount != 0) ??
    [];
  const comparablesWithoutEmptyBuildingCount =
    includedCompProjects?.filter(
      project => project.buildingCount !== null && project.buildingCount !== undefined && project.buildingCount != 0,
    ) ?? [];

  const minGSF = min(comparablesWithoutEmptyGSF, project => project.grossBuildingArea ?? 0);
  const maxGSF = max(comparablesWithoutEmptyGSF, project => project.grossBuildingArea ?? 0);

  const minUnitCount = min(comparablesWithoutEmptyUnitCount, project => project.unitCount ?? 0);
  const maxUnitCount = max(comparablesWithoutEmptyUnitCount, project => project.unitCount ?? 0);

  const minSiteArea = min(comparablesWithoutEmptySiteArea, project => project.siteArea ?? 0);
  const maxSiteArea = max(comparablesWithoutEmptySiteArea, project => project.siteArea ?? 0);

  const minStoryCount = min(comparablesWithoutEmptyStoryCount, project => project.storyCount ?? 0);
  const maxStoryCount = max(comparablesWithoutEmptyStoryCount, project => project.storyCount ?? 0);

  const minBuildingCount = min(comparablesWithoutEmptyBuildingCount, project => project.buildingCount ?? 0);
  const maxBuildingCount = max(comparablesWithoutEmptyBuildingCount, project => project.buildingCount ?? 0);

  const minBudget = min(comparablesWithoutEmptyBudget, project => project[budgetField] ?? 0);
  const maxBudget = max(comparablesWithoutEmptyBudget, project => project[budgetField] ?? 0);

  const minBudgetPerGSF = min(comparablesWithoutEmptyGSF, project => (project[budgetField] ?? 0) / (project.grossBuildingArea ?? 1));
  const maxBudgetPerGSF = max(comparablesWithoutEmptyGSF, project => (project[budgetField] ?? 0) / (project.grossBuildingArea ?? 1));
  const avgBudgetPerGSF = average(comparablesWithoutEmptyGSF, project => (project[budgetField] ?? 0) / (project.grossBuildingArea ?? 1));

  const minBudgetPerNSF = min(comparablesWithoutEmptyNSF, project => (project[budgetField] ?? 0) / (project.netArea ?? 1));
  const maxBudgetPerNSF = max(comparablesWithoutEmptyNSF, project => (project[budgetField] ?? 0) / (project.netArea ?? 1));
  const avgBudgetPerNSF = average(comparablesWithoutEmptyNSF, project => (project[budgetField] ?? 0) / (project.netArea ?? 1));

  const minBudgetPerUnit = min(comparablesWithoutEmptyUnitCount, project => (project[budgetField] ?? 0) / (project.unitCount ?? 1));
  const maxBudgetPerUnit = max(comparablesWithoutEmptyUnitCount, project => (project[budgetField] ?? 0) / (project.unitCount ?? 1));
  const avgBudgetPerUnit = average(comparablesWithoutEmptyUnitCount, project => (project[budgetField] ?? 0) / (project.unitCount ?? 1));

  const minPeriodEnding = minDate(includedCompProjects ?? [], project => new Date(project.maxPeriodEnding ?? ''));
  const maxPeriodEnding = maxDate(includedCompProjects ?? [], project => new Date(project.maxPeriodEnding ?? ''));

  return {
    minGSF,
    maxGSF,
    minUnitCount,
    maxUnitCount,
    minSiteArea,
    maxSiteArea,
    minStoryCount,
    maxStoryCount,
    minBuildingCount,
    maxBuildingCount,
    minBudget,
    maxBudget,
    minBudgetPerGSF,
    maxBudgetPerGSF,
    avgBudgetPerGSF,
    minBudgetPerNSF,
    maxBudgetPerNSF,
    avgBudgetPerNSF,
    minBudgetPerUnit,
    maxBudgetPerUnit,
    avgBudgetPerUnit,
    minPeriodEnding,
    maxPeriodEnding,
  };
};

export const getComparablesLocations = (report: CCReport) => {
  const cityMap = new Map<string, Set<string>>();

  (report.comparableProjects?.filter(comp => comp.isIncluded) ?? [])
    .map(comp => comp.comp)
    .forEach(project => {
      const city = capitalize(project.projectAddress?.city);
      const state = project.projectAddress?.state?.toUpperCase();
      if (city) {
        if (!cityMap.has(city)) {
          cityMap.set(city, new Set());
        }
        if (state) {
          cityMap.get(city)?.add(state);
        }
      }
    });
  const filteredLocations: string[] = [];
  cityMap.forEach((states, city) => {
    if (states.size > 0) {
      states.forEach(state => filteredLocations.push(`${city}, ${state}`));
    } else {
      filteredLocations.push(city);
    }
  });

  return filteredLocations;
};

const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

export const getProjectTypesString = (report: CCReport) => {
  const includedCompProjects = (report.comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);
  const cities = Array.from(new Set(includedCompProjects?.map(project => project.projectType).filter(type => type)));
  return cities.length == 0 ? '--' : cities.join('\n');
};

export const getFiltersObject = (report: CCReport) => {
  if (!report.filtersString) {
    return {};
  }
  return JSON.parse(report.filtersString);
};

export const renderTimePeriod = (start?: Date, end?: Date): string => {
  let result = '';
  if (start && end) {
    const { years, months } = getYearsAndMonthsInBetween(start, end);
    result += ' (';
    if (years > 0) {
      result += years + ' year' + (years > 1 ? 's' : '');
    }
    if (months > 0) {
      if (years > 0) {
        result += ' and ';
      }
      result += months + ' month' + (months > 1 ? 's' : '');
    } else if (years == 0) {
      result += 'less than a month';
    }
    result += ')';
  }
  return result;
};

export const renderDetails = (detailsList: string[]): any => {
  detailsList = Array.from(new Set(detailsList.filter(detailItem => detailItem !== undefined && detailItem !== null && detailItem !== '')));

  if (detailsList.length === 0) {
    return '--';
  }

  const half = Math.ceil(detailsList.length / 2);
  const firstHalf = detailsList.slice(0, half);
  const secondHalf = detailsList.slice(half);

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 6 }}>
        {firstHalf.map((detail, index) => (
          <Typography key={index} variant='body2' fontWeight='regular' sx={{ whiteSpace: 'pre-wrap' }}>
            {detail}
          </Typography>
        ))}
      </Grid>
      <Grid size={{ xs: 6 }}>
        {secondHalf.map((detail, index) => (
          <Typography key={index} variant='body2' fontWeight='regular' sx={{ whiteSpace: 'pre-wrap' }}>
            {detail}
          </Typography>
        ))}
      </Grid>
    </Grid>
  );
};

export const renderCCDescription = (description: string) => {
  const parts = description.split(' in ');
  if (parts.length === 2) {
    return (
      <Box>
        <Stack>
          <Typography variant='body2' fontWeight='bold'>
            {parts[0]}
          </Typography>
          <Typography variant='body2'>in</Typography>
          <Typography variant='body2' fontWeight='bold'>
            {parts[1]}
          </Typography>
        </Stack>
      </Box>
    );
  } else {
    return (
      <Stack>
        <Typography variant='body2' fontWeight='regular' sx={{ whiteSpace: 'pre-wrap' }}>
          {description}
        </Typography>
      </Stack>
    );
  }
};

type MetricType = 'grossBuildingArea' | 'netArea' | 'unitCount';
type LocationType = {
  location: string;
  types: {
    projectType: string;
    avgValue: number;
  }[];
};

export const extractMetricsForGraph = (comparableProjects: CCReportComp[], includeSoftCost: boolean, divisionId?: string) => {
  const includedCompProjects = (comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);
  const usingDivsionBudgets = divisionId != undefined;
  const budgetField = includeSoftCost ? 'totalBudgetWithSoftCost' : 'totalBudgetWithoutSoftCost';

  // Filter out projects with empty or zero metric values
  const filterByMetric = (projects: CCReportCompProject[], metric: MetricType) => {
    return projects.filter(project => {
      const val = project[metric] as number;
      return val !== null && val !== undefined && val !== 0;
    });
  };

  const avgByLocationAndType = (projects: CCReportCompProject[], metric: MetricType): LocationType[] => {
    const filteredProjects = filterByMetric(projects, metric); // Apply filtering

    const grouped = filteredProjects.reduce(
      (acc, project) => {
        const location = project.projectAddress?.city || '';
        const projType = project.projectType || '';

        const capLocation = capitalize(location); // Capitalize location

        // Exclude "Unknown" project types or locations
        if (!capLocation || !projType) return acc;

        if (!acc[capLocation]) {
          acc[capLocation] = {};
        }

        if (!acc[capLocation][projType]) {
          acc[capLocation][projType] = { totalBudget: 0, totalMetric: 0, cnt: 0 };
        }

        const val = project[metric] as number;
        let totalBudget = 0;
        if (usingDivsionBudgets) totalBudget = project.divisionBudgets?.find(d => d.divisionId === divisionId)?.totalBudget ?? 0;
        else totalBudget = project[budgetField] ?? 0;

        if (val && totalBudget) {
          acc[capLocation][projType].totalBudget += totalBudget;
          acc[capLocation][projType].totalMetric += totalBudget / val;
          acc[capLocation][projType].cnt += 1;
        }

        return acc;
      },
      {} as Record<string, Record<string, { totalBudget: number; totalMetric: number; cnt: number }>>,
    );

    // Calculate averages, sort by location and project type
    return Object.keys(grouped)
      .map(location => ({
        location,
        types: Object.keys(grouped[location])
          .sort((a, b) => a.localeCompare(b)) // Sort project types alphabetically
          .map(projType => ({
            projectType: projType,
            avgValue: grouped[location][projType].totalMetric / grouped[location][projType].cnt || 0,
          })),
      }))
      .sort((a, b) => a.location.localeCompare(b.location)); // Sort by location
  };

  // Calculate averages for GSF, NSF, and Unit Count
  const avgGSF = avgByLocationAndType(includedCompProjects, 'grossBuildingArea');
  const avgNSF = avgByLocationAndType(includedCompProjects, 'netArea');
  const avgUnitCount = avgByLocationAndType(includedCompProjects, 'unitCount');

  return {
    avgGSF,
    avgNSF,
    avgUnitCount,
  };
};

export const extractIndividualMetricsForGraph = (
  comparableProjects: CCReportComp[],
  includeSoftCost: boolean,
  limit?: number, // Optional parameter for limiting the results
  divisionId?: string,
) => {
  const includedCompProjects = (comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);
  const usingDivsionBudgets = divisionId != undefined;
  const budgetField = includeSoftCost ? 'totalBudgetWithSoftCost' : 'totalBudgetWithoutSoftCost';

  // Filter out projects where the metric is empty or zero
  const filterByMetric = (projects: CCReportCompProject[], metric: MetricType) => {
    return projects.filter(project => {
      const value = project[metric] as number;
      return value !== null && value !== undefined && value !== 0;
    });
  };

  // Calculate averages by location
  const avgByLocation = (projects: CCReportCompProject[], metric: MetricType) => {
    const filteredProjects = filterByMetric(projects, metric); // Apply the filtering

    const grouped = filteredProjects.reduce(
      (acc, project) => {
        const location = project.projectAddress?.city || '';
        const capitalizedLocation = capitalize(location);

        // Skip if location is empty (i.e., "Unknown")
        if (!capitalizedLocation) {
          return acc;
        }

        if (!acc[capitalizedLocation]) {
          acc[capitalizedLocation] = { totalBudget: 0, totalMetric: 0, count: 0 };
        }

        const value = project[metric] as number;
        let totalBudget = 0;
        if (usingDivsionBudgets) {
          totalBudget = project.divisionBudgets?.find(d => d.divisionId === divisionId)?.totalBudget ?? 0;
        } else totalBudget = project[budgetField] ?? 0;

        if (value && totalBudget) {
          acc[capitalizedLocation].totalBudget += totalBudget;
          acc[capitalizedLocation].totalMetric += totalBudget / value; // Budget per metric (GSF, NSF, or unit count)
          acc[capitalizedLocation].count += 1;
        }

        return acc;
      },
      {} as Record<string, { totalBudget: number; totalMetric: number; count: number }>,
    );

    let results = Object.keys(grouped)
      .map(location => ({
        location,
        avgValue: grouped[location].totalMetric / grouped[location].count || 0,
      }))
      .filter(result => result.avgValue !== 0);

    // // Sort by avgValue (descending) to get the highest avgValue first
    // results = results.sort((a, b) => b.avgValue - a.avgValue);

    // Sort alphabetically by location
    results = results.sort((a, b) => a.location.localeCompare(b.location));

    return limit ? results.slice(0, limit) : results;
  };

  // Calculate averages by project type
  const avgByProjectType = (projects: CCReportCompProject[], metric: MetricType) => {
    const filteredProjects = filterByMetric(projects, metric); // Apply the filtering

    const grouped = filteredProjects.reduce(
      (acc, project) => {
        const projectType = project.projectType || '';
        const capitalizedProjectType = capitalize(projectType);

        // Skip if projectType is empty (i.e., "Unknown")
        if (!capitalizedProjectType) {
          return acc;
        }

        if (!acc[capitalizedProjectType]) {
          acc[capitalizedProjectType] = { totalBudget: 0, totalMetric: 0, count: 0 };
        }

        const value = project[metric] as number;
        let totalBudget = 0;
        if (usingDivsionBudgets) totalBudget = project.divisionBudgets?.find(d => d.divisionId === divisionId)?.totalBudget ?? 0;
        else totalBudget = project[budgetField] ?? 0;

        if (value && totalBudget) {
          acc[capitalizedProjectType].totalBudget += totalBudget;
          acc[capitalizedProjectType].totalMetric += totalBudget / value; // Budget per metric (GSF, NSF, or unit count)
          acc[capitalizedProjectType].count += 1;
        }

        return acc;
      },
      {} as Record<string, { totalBudget: number; totalMetric: number; count: number }>,
    );

    let results = Object.keys(grouped)
      .map(projectType => ({
        projectType,
        avgValue: grouped[projectType].totalMetric / grouped[projectType].count || 0,
      }))
      .filter(result => result.avgValue !== 0);

    // // Sort by avgValue (descending) to get the highest avgValue first
    // results = results.sort((a, b) => b.avgValue - a.avgValue);

    // Sort alphabetically by project type
    results = results.sort((a, b) => a.projectType.localeCompare(b.projectType));

    return limit ? results.slice(0, limit) : results;
  };

  // Calculate averages for GSF, NSF, and Unit Count
  const avgGSFByLocation = avgByLocation(includedCompProjects, 'grossBuildingArea');
  const avgGSFByType = avgByProjectType(includedCompProjects, 'grossBuildingArea');

  const avgNSFByLocation = avgByLocation(includedCompProjects, 'netArea');
  const avgNSFByType = avgByProjectType(includedCompProjects, 'netArea');

  const avgUnitCountByLocation = avgByLocation(includedCompProjects, 'unitCount');
  const avgUnitCountByType = avgByProjectType(includedCompProjects, 'unitCount');

  return {
    avgGSFByLocation,
    avgGSFByType,
    avgNSFByLocation,
    avgNSFByType,
    avgUnitCountByLocation,
    avgUnitCountByType,
  };
};

// Main function to generate the static map URL
export const generateStaticMapUrl = (report: CCReport): string | null => {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const baseUrl = process.env.REACT_APP_GOOGLE_MAPS_BASE_URL || '';

  const includedComps = report.comparableProjects?.filter(
    comp => comp.isIncluded && comp.comp.projectAddress?.latitude !== null && comp.comp.projectAddress?.longitude !== null,
  );

  if (!includedComps || includedComps.length === 0) {
    return null;
  }

  const latitudes = includedComps.map(comp => comp.comp.projectAddress!.latitude).filter((lat): lat is number => lat !== undefined);
  const longitudes = includedComps.map(comp => comp.comp.projectAddress!.longitude).filter((lng): lng is number => lng !== undefined);

  const mapDimensions = { width: 600, height: 400 };
  const mapCenterAndZoom = calculateMapCenterAndZoom(latitudes, longitudes, mapDimensions.width, mapDimensions.height);

  if (!mapCenterAndZoom) return null;

  const { center, zoomLevel } = mapCenterAndZoom;

  const markers = includedComps
    .map(comp => `markers=color:red%7C${comp.comp.projectAddress!.latitude},${comp.comp.projectAddress!.longitude}`)
    .join('&');

  const mapCenter = `center=${center.lat},${center.lng}`;
  const mapSize = `size=${mapDimensions.width}x${mapDimensions.height}`;
  const zoomParam = `zoom=${zoomLevel}`;

  const mapUrl = `${baseUrl}?${mapCenter}&${zoomParam}&${mapSize}&${markers}&key=${apiKey}`;

  return mapUrl;
};

export const extractMetricsForScatterPlot = (comparableProjects: CCReportComp[], includeSoftCost: boolean, divisionId?: string) => {
  const includedCompProjects = (comparableProjects?.filter(comp => comp.isIncluded) ?? []).map(comp => comp.comp);
  const usingDivisionBudgets = divisionId != undefined;
  const budgetField = includeSoftCost ? 'totalBudgetWithSoftCost' : 'totalBudgetWithoutSoftCost';

  // Helper function to process data based on the metric type
  const extractData = (projects: CCReportCompProject[], metric: keyof CCReportCompProject) => {
    return projects
      .filter(project => {
        return (
          project?.commencementDate && // Ensure commencement date exists
          project[metric] !== null &&
          project[metric] !== undefined &&
          project[metric] !== 0 // Ensure metric has a valid value
        );
      })
      .map(project => {
        const commencementDate = project.commencementDate!;
        const value = project[metric] as number;
        let totalBudget = 0;

        if (usingDivisionBudgets) {
          totalBudget = project.divisionBudgets?.find(d => d.divisionId === divisionId)?.totalBudget ?? 0;
        } else {
          totalBudget = project[budgetField] ?? 0;
        }

        return {
          date: commencementDate, // X-axis (full date for accurate plotting)
          costValue: totalBudget > 0 ? totalBudget / value : 0, // Y-axis (cost per unit)
          compName: project.projectTitle,
        };
      })
      .filter(data => data.costValue !== 0) // Filter out zero values
      .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime()); // Sort by date
  };

  return {
    avgByGrossSF: extractData(includedCompProjects, 'grossBuildingArea'),
    avgByNetSF: extractData(includedCompProjects, 'netArea'),
    avgByUnitCount: extractData(includedCompProjects, 'unitCount'),
  };
};

export const captureScatterPlot = async (ref: React.MutableRefObject<HTMLDivElement | null>) => {
  try {
    if (!ref?.current) {
      console.warn('Scatter plot ref is null or unmounted:', ref);
      return undefined;
    }

    let element = ref.current;

    // Ensure the scatter plot has dimensions before capturing
    while (element && element.getBoundingClientRect().width === 0) {
      console.warn('Waiting for scatter plot to render...');
      await new Promise(resolve => requestAnimationFrame(resolve));
      element = ref.current; // Re-check ref after next frame
    }

    if (!element || element.getBoundingClientRect().width === 0) {
      console.error('Scatter plot has zero dimensions or ref is stale. Cannot capture.');
      return undefined;
    }

    // Ensure the DOM updates are applied before capturing
    await new Promise(resolve => requestAnimationFrame(resolve));

    const canvas = await html2canvas(element, { scale: 2 });
    return canvas.toDataURL('image/png');
  } catch (err) {
    console.error('Error capturing scatter plot:', err);
    return undefined;
  }
};
